import React, { useMemo } from 'react';
import { isNull, noop } from 'lodash/fp';

import { ModalHeader } from '../../shared/modal/ModalHeader';
import { SmileyNegative, SmileyNeutral, SmileyPositive } from '../../shared/icons';
import styles from './NettingEngine.module.scss';
import { SelectedJurisdictionDetails } from './store';
import { PositionModal } from '../../shared/modal/PositionModal';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { SpaceBetweenButtonWrapper } from '../../shared/button/SpaceBetweenButtonWrapper';
import { Button } from '../../shared/button/Button';
import { WYSIWYG } from '../../shared/wysiwyg/WYSIWYG';
import { ANALYSIS_VIEW_BUTTONS } from '../../../hooks/useSplitView';

const { fixedPrimary, green, amber, red } = styles;

const TILE_PADDING = 11;
const CAROUSEL_ARROW = 28;
const PAGE_PADDING = 10;
const NAV_MENU = 100;
const APP_BODY_TOP_PADDING = 10;
const PAGE_TITLE = 62;
const ANALYSIS_SUMMARY_HEADER = 39;
const ANALYSIS_SECTION_PADDING = 21;
const COUNTRY_HEADER = 27;
const MODAL_PADDING = 10;
const MODAL_HEADER = 56;
const BUTTON_WRAPPER = 48;

interface NettingEngineAnswerJustificationModalProps {
    isOpen: boolean;
    answerDetails: SelectedJurisdictionDetails;
    closeModal: () => void;
    width: number;
    height: number;
    isInstanceView: boolean;
}

export const NettingEngineAnswerJustificationModal: React.FC<NettingEngineAnswerJustificationModalProps> = ({ isOpen, answerDetails, closeModal, width, height, isInstanceView }) => {
    const { details, icon, value, question } = answerDetails;

    const iconToDisplay = useMemo(() => {
        switch (icon) {
            case 'positive':
                return { selectedIcon: SmileyPositive, colour: green };
            case 'negative':
                return { selectedIcon: SmileyNegative, colour: red };
            default:
                return { selectedIcon: SmileyNeutral, colour: amber };
        }
    }, [icon]);

    const initialWidthOffset = useMemo(() => TILE_PADDING + CAROUSEL_ARROW + PAGE_PADDING + (isInstanceView ? ANALYSIS_VIEW_BUTTONS : 0), [isInstanceView]);
    const initialHeightOffset = NAV_MENU + APP_BODY_TOP_PADDING + PAGE_TITLE + ANALYSIS_SUMMARY_HEADER + ANALYSIS_SECTION_PADDING + COUNTRY_HEADER;
    const scrollableHeight = useMemo(() => height - (MODAL_PADDING + MODAL_HEADER + BUTTON_WRAPPER), [height]);

    const body = useMemo(() => (
        <div className={styles.detailsSummary}>
            <div className={styles.questionValue}>{question}</div>
            {<div className={styles.contentValue}>{value}</div>}
            {isNull(details) ? <div className={styles.contentDetails}>There is no additional information</div> :
                <>
                    <div className={styles.contentDetails}>Details:</div>
                    <WYSIWYG
                        isReadOnly
                        updateContent={noop}
                        content={details}
                        toolbarHidden
                        showBorder={false}
                        showWrapperBorder={false}
                        height='100%'
                        maxHeight='100%'
                        minHeight='100%'
                    />
                </>
            }
        </div>
    ), [details, question, value]);

    return (
        <PositionModal
            closeModal={closeModal}
            isOpen={isOpen}
            position={{ x: initialWidthOffset, y: initialHeightOffset }}
            height='fit-content'
            width={`${width}px`}
            testId='netting-engine-answer-justification-modal'
            zIndex={12}
        >
            <ModalHeader
                testId='netting-engine-answer-justification-modal'
                label='Justification'
                icon={iconToDisplay.selectedIcon}
                iconColor={fixedPrimary}
                iconFill={iconToDisplay.colour}
            />
            <Scrollable maxHeight={`${scrollableHeight}px`}>
                {body}
            </Scrollable>
            <SpaceBetweenButtonWrapper rightButtons={[<Button label='Close' onClick={closeModal} key='close-modal' />]} leftButtons={[]} />
        </PositionModal>
    );
};
